import React, { useState, useEffect } from 'react';
import MaterialTable from '../components/Table/ResponsiveMaterialTable';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { WEB_SECONDORY_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import localization from '../components/Table/Localization';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ImageIcon from '@mui/icons-material/Image';

const STATUS_COLORS = {
  review:   { bg: '#FEF3C7', color: '#D97706' },
  approved: { bg: '#D1FAE5', color: '#059669' },
  rejected: { bg: '#FEE2E2', color: '#DC2626' },
};

const OfflineTopups = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const offlinetopupdata = useSelector((state) => state.offlinetopupdata);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);

  const [data, setData] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, topup: null });
  const [rejectReason, setRejectReason] = useState('');
  const [imageDialog, setImageDialog] = useState({ open: false, url: null });

  useEffect(() => {
    dispatch(api.fetchAllOfflineTopups());
    return () => dispatch(api.stopFetchAllOfflineTopups());
  }, []);

  useEffect(() => {
    if (offlinetopupdata.topups) {
      setData(offlinetopupdata.topups);
    } else {
      setData([]);
    }
  }, [offlinetopupdata.topups]);

  function formatAmount(value) {
    const number = parseFloat(value || 0);
    const decimal = settings?.decimal || 2;
    const country = settings?.country;
    if (country === 'Vietnam') {
      return number.toLocaleString('vi-VN', { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
    }
    return number.toLocaleString('en-US', { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
  }

  const handleApprove = (topup) => {
    dispatch(api.approveOfflineTopup(topup, auth.profile.uid));
  };

  const handleRejectConfirm = () => {
    if (rejectDialog.topup) {
      dispatch(api.rejectOfflineTopup(rejectDialog.topup, auth.profile.uid, rejectReason));
    }
    setRejectDialog({ open: false, topup: null });
    setRejectReason('');
  };

  const columns = [
    { title: t('requestDate'), field: 'createdAt', defaultSort: 'desc',
      render: (row) => row.createdAt ? moment(row.createdAt).format('lll') : '-',
    },
    { title: t('driver_name'), field: 'driverName', editable: 'never' },
    { title: t('driver_phone'), field: 'driverPhone', editable: 'never' },
    { title: t('amount'), field: 'amount', editable: 'never',
      render: (row) =>
        settings?.swipe_symbol
          ? formatAmount(row.amount) + ' ' + settings.symbol
          : settings?.symbol + ' ' + formatAmount(row.amount),
    },
    { title: t('offline_topup_operator'), field: 'selectedMethod', editable: 'never',
      render: (row) => row.selectedMethod || '-',
    },
    { title: t('offline_topup_transaction_code'), field: 'transactionCode', editable: 'never' },
    { title: t('status'), field: 'status', editable: 'never',
      render: (row) => {
        const sc = STATUS_COLORS[row.status] || STATUS_COLORS.review;
        return (
          <Chip
            label={t('offline_topup_status_' + row.status)}
            size="small"
            style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700 }}
          />
        );
      },
    },
    { title: t('proof'), field: 'proofImageUrl', editable: 'never',
      render: (row) =>
        row.proofImageUrl ? (
          <Button
            size="small"
            startIcon={<ImageIcon />}
            onClick={() => setImageDialog({ open: true, url: row.proofImageUrl })}
          >
            {t('view')}
          </Button>
        ) : '-',
    },
    { title: t('offline_topup_reject_reason'), field: 'rejectionReason', editable: 'never',
      render: (row) => row.rejectionReason || '-',
    },
    { title: t('processDate'), field: 'processedAt',
      render: (row) => row.processedAt ? moment(row.processedAt).format('lll') : '-',
    },
    { title: t('actions'), field: 'actions', editable: 'never', sorting: false, filtering: false,
      render: (row) =>
        row.status === 'review' ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => handleApprove(row)}
            >
              {t('approve')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => setRejectDialog({ open: true, topup: row })}
            >
              {t('reject')}
            </Button>
          </Box>
        ) : null,
    },
  ];

  return (
    offlinetopupdata.loading ? <CircularLoading /> :
    <ThemeProvider theme={theme}>
      <MaterialTable
        title={t('offline_topup_requests')}
        columns={columns}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '8px',
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: '20px',
        }}
        data={data}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 25, 50],
          exportButton: true,
          rowStyle: (rowData) => ({
            backgroundColor:
              rowData.status === 'approved' ? '#F0FDF4' :
              rowData.status === 'rejected' ? '#FFF5F5' : '#FFFBEB',
          }),
          filtering: true,
        }}
        localization={localization(t, isRTL)}
      />

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, topup: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('offline_topup_reject_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('offline_topup_reject_reason_label')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('optional')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, topup: null })}>{t('cancel')}</Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">
            {t('reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proof image dialog */}
      <Dialog open={imageDialog.open} onClose={() => setImageDialog({ open: false, url: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('proof')}</DialogTitle>
        <DialogContent>
          {imageDialog.url && (
            <img
              src={imageDialog.url}
              alt="proof"
              style={{ width: '100%', borderRadius: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog({ open: false, url: null })}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default OfflineTopups;
